import "./ProjectInfo.scss";
import { ReactComponent as Cross } from "./../../assets/images/cross.svg";
import { ReactComponent as ArrowRight } from "./../../assets/images/arrow-right.svg";
import { Link, useLocation, useRouter } from "wouter";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { useEffect, useRef } from "react";
import { LegalLinks } from "../LegalLinks";
import { disableBodyScroll, clearAllBodyScrollLocks } from "body-scroll-lock";
import { pageView } from "../../utils/analytics";

export function ProjectInfo() {
	let { base } = useRouter();
	const [location, updateLocation] = useLocation();
	let back = "/" + location.split("/")[1];

	const containerRef = useRef();

	useEffect(() => {
		pageView(
			`${base}/tech-radar/project-info`,
			"Project Info - The Virtual Economy Technology Radar | L'Atelier"
		);
	}, [base]);

	useEffect(() => {
		document.documentElement.classList.add("modal-active");
		disableBodyScroll(containerRef.current);
		return () => {
			clearAllBodyScrollLocks();
			document.documentElement.classList.remove("modal-active");
		};
	}, [containerRef]);

	useEffect(() => {
		let cb = (e) => {
			if (e.code === "Escape" && !e.metaKey) {
				updateLocation(back);
			}
		};
		window.addEventListener("keyup", cb);
		return () => {
			window.removeEventListener("keyup", cb);
		};
	}, [back, updateLocation]);

	return (
		<>
			<motion.div
				initial={{ opacity: 0 }}
				animate={{ opacity: 1 }}
				exit={{ opacity: 0 }}
				transition={{ duration: 0.3 }}
				className={"project-info-bg"}></motion.div>
			<div
				className={"project-info"}
				ref={containerRef}
				onClick={(e) => {
					if (e.target && e.target.classList.contains("project-info")) {
						updateLocation(back);
					}
				}}>
				<Helmet>
					<title>
						Project Info - The Virtual Economy Technology Radar | L'Atelier
					</title>
					<link rel={"canonical"} href={`${base}/tech-radar/project-info`} />
				</Helmet>

				<motion.div
					initial={{ transform: "translateX(100%)" }}
					animate={{ transform: "translateX(0%)" }}
					exit={{ transform: "translateX(100%)" }}
					transition={{ duration: 0.3 }}
					className={"project-info__container"}>
					<div className={"project-info__navigation"}>
						<div className={"project-info__close-container"}>
							<Link className={"project-info__close"} href={back}>
								<span className={"visually-hidden"}>Close</span>
								<Cross />
							</Link>
						</div>
					</div>
					<div className={"project-info__content"}>
						<h1 className="project-info__content__title">About This Project</h1>
						<div className="project-info__content__content">
							<p>
								Capturing the development and growth of emerging technology
								requires foresight analysis across a broad spectrum of low
								signals. L'Atelier has developed a data intelligence engine
								capable of automating data gathering at key points of the
								technology journey - from idea to market. Through hundreds of
								millions of data points, the engine can assess the significance
								of any given technology domain across the world.
							</p>

							<p>
								This engine powers our{" "}
								<strong>Virtual Economy Technology Radar</strong>, an
								interactive map of 54 of the key emerging technologies
								underpinning the virtual economy - their current capabilities,
								likely trajectory, and research ecosystem. These technologies
								are clustered into three categories tracking the user journey in
								the virtual economy: from <strong>Access</strong>, through{" "}
								<strong>Immersion</strong>, to <strong>Transaction</strong>.
							</p>

							<p>
								The diagram below outlines a theoretical trajectory of emerging
								technology. This development path can vary by technology. We
								have invented four algorithms that score the level of activity
								at each stage. The Grants, Research, Patents and Investments
								scores capture trends across a broad range of relevant signals.
							</p>

							<p>
								The scores can be compiled to estimate the significance of a
								given emerging technology. The higher the overall score, the
								more likely that researchers, R&D departments, and investors are
								actively engaged in creating new opportunity spaces around each
								technology.
							</p>
						</div>
						<p className="project-info__content__fig-text">
							Fig.1 The trajectory of emerging tech from idea to market
							opportunity
						</p>
						<div className="project-info__content__chart">
							<div className="project-info__content__chart__column-title">
								<p>Stakeholders</p>
							</div>
							<div className="project-info__content__chart__row project-info__content__chart__row--1">
								<div className="project-info__content__chart__row__title">
									<div className="project-info__content__chart__row__title__inner">
										<div className="project-info__content__chart__row__title__number">
											1
										</div>
										Grants
									</div>
								</div>
								<div className="project-info__content__chart__row__stakeholders">
									<ul>
										<li>Governments</li>
										<li>Corporations</li>
										<li>Foundations</li>
									</ul>
								</div>
							</div>
							<div className="project-info__content__chart__row  project-info__content__chart__row--2">
								<div className="project-info__content__chart__row__title">
									<div className="project-info__content__chart__row__title__inner">
										<div className="project-info__content__chart__row__title__number">
											2
										</div>
										Research
									</div>
								</div>
								<div className="project-info__content__chart__row__stakeholders">
									<ul>
										<li>Academic departments</li>
										<li>Research groups</li>
									</ul>
								</div>
							</div>
							<div className="project-info__content__chart__row  project-info__content__chart__row--3">
								<div className="project-info__content__chart__row__title">
									<div className="project-info__content__chart__row__title__inner">
										<div className="project-info__content__chart__row__title__number">
											3
										</div>
										Patents
									</div>
								</div>
								<div className="project-info__content__chart__row__stakeholders">
									<ul>
										<li>Universities</li>
										<li>R&D (private)</li>
										<li>Governments</li>
									</ul>
								</div>
							</div>
							<div className="project-info__content__chart__row  project-info__content__chart__row--4">
								<div className="project-info__content__chart__row__title">
									<div className="project-info__content__chart__row__title__inner">
										<div className="project-info__content__chart__row__title__number">
											4
										</div>
										Investments
									</div>
								</div>
								<div className="project-info__content__chart__row__stakeholders">
									<ul>
										<li>Capital holders</li>
										<li>Venture capital</li>
										<li>Angel investors</li>
										<li>Private equity</li>
									</ul>
								</div>
							</div>
							<div className="project-info__content__chart__end">
								<div className="project-info__content__chart__end__text">
									<div className="project-info__content__chart__end__text__inner">
										<div className="project-info__content__chart__end__text__arrow">
											<ArrowRight />
										</div>
										Emerging tech reaches market
									</div>
								</div>
							</div>
						</div>
						<h1 className="project-info__content__title">Credits</h1>
						<div className="project-info__content__content">
							<p>
								<strong>Nadya Ivanova</strong> - Producer & Editor
								<br />
								<strong>Aurore Geraud</strong> - Researcher
								<br />
								<strong>Giorgio Tarraf</strong> - Project Manager, Technology
								Intelligence Engine
								<br />
								<strong>Dave Herman</strong> - Design & Development, Technology
								Intelligence Engine
							</p>
							<p>
								<a href="https://gladeye.com/" target="_blank" rel="noreferrer">
									<strong>Gladeye</strong>
								</a>{" "}
								- Design & Development by Gladeye
							</p>

							<p>
								<a href="https://atelier.net/" target="_blank" rel="noreferrer">
									L'Atelier
								</a>{" "}
								is a foresight business that identifies and contextualises
								future market opportunities that emerge from technological and
								societal change.
							</p>

							<p>
								We explore the big questions that will transform our world
								through an interdisciplinary approach that combines data-driven
								research with visual storytelling. We are an independent
								subsidiary of BNP Paribas Group.
							</p>
						</div>
						<div className="project-info__legal-links">
							<LegalLinks />
						</div>
					</div>
				</motion.div>
			</div>
		</>
	);
}
